import React, { useState, useEffect, useRef } from 'react';
import Grid from 'components/elements/Grid';
import FieldSetWithTitle from 'shared/FieldSetWithTitle/components/FieldSetWithTitle';
import { OpportunitySelectGroup, OpportunityTextInput } from '../OpportunityFields';
import { GaugeChart } from 'shared/GaugeChart';
import Checkbox from 'shared/Checkbox';
import ControlledSearchSelectGroup from 'components/shared/form/ControlledSearchSelectGroup';

interface IThirdPartyShipper {
  id: number;
  value: string;
  country_id: number;
}

type OpportunityMode = '' | 'by_profile' | 'by_quality';

export const OpportunityCoffee = ({
  originCountryIdsField,
  originCountryIds,
  setOriginCountryIds,
  thirdPartyShipperField,
  markIdField,
  qualityIdField,
  categoryIdField,
  profileIdField,
  certificateIdField,
  estimatedKgField,
  excelsoEstimateQualityIdField,
  contractType,
  onAvailableWeightChange,
}) => {
  const [thirdPartyShipper, setThirdPartyShipper] = useState<IThirdPartyShipper>();
  const [availableWeight, setAvailableWeight] = useState<number | null>(null);
  const [totalWeight, setTotalWeight] = useState<number | null>(null);
  const [inventoryQueried, setInventoryQueried] = useState(false);
  const [categoryId, setCategoryId] = useState(categoryIdField?.value);
  const [profileId, setProfileId] = useState(profileIdField?.value);
  const [qualityId, setQualityId] = useState(qualityIdField?.value);
  const [markId, setMarkId] = useState(markIdField?.value);
  const [qualityOptions, setQualityOptions] = useState(qualityIdField?.options);
  const [profileOptions, setProfileOptions] = useState(profileIdField?.options);
  const [markOptions, setMarkOptions] = useState(markIdField?.options);
  const [countryId, setCountryId] = useState(originCountryIds[0]);
  const [excelsoEstimateQualityId, setExcelsoEstimateQualityId] = useState(
    excelsoEstimateQualityIdField?.value
  );

  // Determine initial mode: default to by_quality when creating; restore from values on edit
  const getInitialMode = (): OpportunityMode => {
    if (contractType !== 'fwd') return '';
    if (profileIdField?.value) return 'by_profile';
    return 'by_quality';
  };
  const [opportunityMode, setOpportunityMode] = useState<OpportunityMode>(getInitialMode);

  useEffect(() => {
    onAvailableWeightChange?.(availableWeight);
  }, [availableWeight]);

  // When contract type changes to fwd, default to by_quality if no mode is set yet
  useEffect(() => {
    if (contractType === 'fwd' && !opportunityMode) {
      setOpportunityMode('by_quality');
    } else if (contractType !== 'fwd') {
      setOpportunityMode('');
    }
  }, [contractType]);

  const disableMark = originCountryIds.length > 1;
  const initialQualityOptionsRef = useRef(
    qualityIdField?.options ? [...qualityIdField.options] : []
  );
  const getCountryIdFromProfile = () => {
    return (
      profileOptions.find((profile) => profile.id === parseInt(profileId, 10))
        ?.country_id || null
    );
  };

  const handleModeChange = (mode: OpportunityMode) => {
    setOpportunityMode(mode);
    // Reset all dependent fields
    setCategoryId('');
    setQualityId('');
    setProfileId('');
    setMarkId('');
    setProfileOptions([]);
    setMarkOptions([]);
    setQualityOptions(initialQualityOptionsRef.current);
    setAvailableWeight(null);
    setTotalWeight(null);
  };

  // Fetch quality options whenever category changes (also runs on mount in edit)
  useEffect(() => {
    if (!categoryId) {
      setQualityOptions(initialQualityOptionsRef.current);
      return;
    }

    setQualityOptions([]);
    let active = true;
    fetch(`/api/v1/categories/${categoryId}`)
      .then((res) => res.json())
      .then((data) => {
        if (active) setQualityOptions(data);
      });
    return () => {
      active = false;
    };
  }, [categoryId]);

  // Fetch profile options whenever category or country changes
  useEffect(() => {
    if (opportunityMode !== 'by_profile' || !countryId || !categoryId) {
      if (opportunityMode === 'by_profile') setProfileOptions([]);
      return;
    }

    setProfileOptions([]);
    let active = true;
    fetch(`/api/v1/profiles?category_id=${categoryId}&country_id=${countryId}`)
      .then((res) => res.json())
      .then((data) => {
        if (active) setProfileOptions(Array.isArray(data) ? data : []);
      });
    return () => {
      active = false;
    };
  }, [categoryId, countryId, opportunityMode]);

  // When profile is selected in by_profile mode, set qualityId from profile's quality
  useEffect(() => {
    if (opportunityMode !== 'by_profile' || !profileId) return;

    const selectedProfile = profileOptions.find((p) => p.id === parseInt(profileId, 10));
    if (selectedProfile?.quality_id) {
      setQualityId(String(selectedProfile.quality_id));
    }
  }, [profileId, profileOptions, opportunityMode]);

  // PLA-1040: Marca options.
  //  - by_profile: marks of the selected profile (unchanged).
  //  - by_quality: marks of the selected origin (country), regardless of quality.
  useEffect(() => {
    if (contractType !== 'fwd') return;
    let active = true;

    if (opportunityMode === 'by_profile' && profileId) {
      setMarkOptions([]);
      fetch(`/api/v1/profiles/${profileId}`)
        .then((res) => res.json())
        .then((data) => {
          if (active) setMarkOptions(data);
        });
    } else if (opportunityMode === 'by_quality' && countryId) {
      setMarkOptions([]);
      fetch(`/api/v1/marks?country_id=${countryId}`)
        .then((res) => res.json())
        .then((data) => {
          if (active) setMarkOptions(data);
        });
    } else {
      setMarkOptions([]);
    }

    return () => {
      active = false;
    };
  }, [profileId, countryId, opportunityMode, contractType]);

  const handleCategorySelect = (value: string) => {
    setCategoryId(value || '');
    setProfileId('');
    setMarkId('');
    setProfileOptions([]);
    setMarkOptions([]);
  };

  const handleQualitySelect = (value: string) => {
    // PLA-1040: la lista de marcas ya no depende de la calidad, así que la
    // marca elegida se conserva al cambiar la calidad.
    setQualityId(value);
  };

  const handleProfileSelect = (value: string) => {
    setProfileId(value);
    setMarkId('');
  };

  const handleMarkSelect = (value: string) => {
    setMarkId(value);
  };

  const applyInactiveProfileLabels = (
    options: any[] | undefined,
    selectedId: string | number | undefined | null
  ) => {
    if (!options) return options;
    const inactiveSuffix = ` (${I18n.translate('common.inactive')})`;
    return options
      .filter((o: any) => {
        const isActive = o?.active !== false;
        const isCurrentlyAssigned =
          selectedId !== undefined &&
          selectedId !== null &&
          selectedId !== '' &&
          String(o?.id) === String(selectedId);
        return isActive || isCurrentlyAssigned;
      })
      .map((o: any) => {
        const isActive = o?.active !== false;
        return isActive ? o : { ...o, name: `${o.name}${inactiveSuffix}` };
      });
  };

  let filteredMarks = markIdField?.options;

  if (originCountryIds.length == 1 || thirdPartyShipper) {
    filteredMarks = filteredMarks.filter(
      (mark) =>
        (originCountryIds.length == 1 &&
          mark.countryId == parseInt(originCountryIds[0])) ||
        thirdPartyShipper?.country_id === mark.countryId
    );
  }
  if (disableMark) {
    filteredMarks = [];
  }

  const handleThirdPartySelect = (value: string) => {
    const newThirdPartyShipper = thirdPartyShipperField.options.find(
      (tps: IThirdPartyShipper) => tps.id === parseInt(value, 10)
    );
    setThirdPartyShipper(newThirdPartyShipper);
  };

  useEffect(() => {
    let isMounted = true;
    const resolvedCountryId = getCountryIdFromProfile() || countryId;

    if (!qualityId || !resolvedCountryId) {
      setAvailableWeight(null);
      setTotalWeight(null);
      setInventoryQueried(false);
      return;
    }

    setInventoryQueried(true);

    const fetchData = async () => {
      try {
        const response = await fetch(
          `/api/v1/excelso_estimate_qualities?quality_id=${qualityId}&country_id=${resolvedCountryId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (isMounted) {
            const available =
              data.available_weight !== null && data.available_weight !== undefined
                ? Number(data.available_weight)
                : null;
            const total =
              data.total_weight !== null && data.total_weight !== undefined
                ? Number(data.total_weight)
                : null;
            setAvailableWeight(available);
            setTotalWeight(total);
            setExcelsoEstimateQualityId(data.excelso_estimate_quality_id);
          }
        } else {
          if (isMounted) {
            setAvailableWeight(null);
            setTotalWeight(null);
          }
        }
      } catch (error) {
        console.error('Error fetching available weight:', error);
        if (isMounted) {
          setAvailableWeight(null);
          setTotalWeight(null);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [categoryId, profileId, qualityId, countryId, profileOptions]);

  const isFwd = contractType === 'fwd';
  const isByProfile = opportunityMode === 'by_profile';
  const isByQuality = opportunityMode === 'by_quality';

  return (
    <FieldSetWithTitle title={I18n.translate('opportunities.form.coffee')}>
      <Grid>
        {contractType === 'third_party' && (
          <OpportunitySelectGroup
            field={{
              ...thirdPartyShipperField,
              options: thirdPartyShipperField?.options?.sort((a, b) =>
                a.name.localeCompare(b.name)
              ),
            }}
            value={thirdPartyShipper?.id || ''}
            onChange={handleThirdPartySelect}
            required={contractType === 'third_party'}
            showAsterisk={false}
          />
        )}
        {isFwd && (
          <Checkbox
            icon='tick'
            inline={true}
            asRadioButton={true}
            checked={isByQuality}
            required={true}
            label={I18n.translate('opportunities.form.opportunity_by_quality')}
            onChange={() => handleModeChange('by_quality')}
          />
        )}
        {isFwd && (
          <Checkbox
            icon='tick'
            inline={true}
            asRadioButton={true}
            checked={isByProfile}
            required={true}
            label={I18n.translate('opportunities.form.opportunity_by_profile')}
            onChange={() => handleModeChange('by_profile')}
          />
        )}
        {isFwd && (
          <input
            type='hidden'
            name='opportunity[opportunity_mode]'
            value={opportunityMode}
          />
        )}
        {isFwd && <div style={{ gridColumn: '1 / -1', height: '1.5em' }} />}
        {isFwd && (
          <OpportunitySelectGroup
            field={originCountryIdsField}
            value={originCountryIds}
            onChange={(value) => {
              setOriginCountryIds(value);
              setCountryId(value[0]);
              // PLA-1040: una marca de otro país deja de ser válida al cambiar origen.
              setMarkId('');
            }}
            multiple={true}
            overrideName={'opportunity[origin_country_ids][]'}
            required={true}
            showAsterisk={false}
          />
        )}
        {contractType === 'local' && (
          <OpportunitySelectGroup
            field={originCountryIdsField}
            value={
              originCountryIds.length > 0
                ? originCountryIds[0]
                : originCountryIdsField.options[0].id
            }
            onChange={(id) => setOriginCountryIds([id])}
            overrideName={'opportunity[origin_country_ids][]'}
          />
        )}

        {isFwd && isByProfile && (
          <OpportunitySelectGroup
            field={{ ...categoryIdField, required: true }}
            value={categoryId}
            onChange={(value) => handleCategorySelect(value)}
          />
        )}
        {isFwd && (
          <input
            type='hidden'
            name='opportunity[excelso_estimate_quality_id]'
            value={excelsoEstimateQualityId}
          />
        )}
        {isFwd && isByProfile && (
          <OpportunitySelectGroup
            field={{ ...profileIdField, required: true }}
            value={profileId}
            onChange={(value) => handleProfileSelect(value)}
            overrideOptions={applyInactiveProfileLabels(profileOptions, profileId)}
          />
        )}
        {isFwd && isByProfile && (
          <input type='hidden' name='opportunity[quality_id]' value={qualityId} />
        )}
        {isFwd && isByQuality && (
          <OpportunitySelectGroup
            field={{ ...qualityIdField, required: true }}
            value={qualityId}
            onChange={(value) => handleQualitySelect(value)}
            overrideOptions={qualityOptions}
          />
        )}
        {!isFwd && (
          <OpportunitySelectGroup
            field={qualityIdField}
            value={qualityId}
            onChange={(value) => handleQualitySelect(value)}
            overrideOptions={qualityOptions}
            required={contractType !== 'spot'}
            showAsterisk={false}
            acceptEmptyValue
          />
        )}
        {inventoryQueried &&
          (totalWeight !== null &&
          typeof totalWeight === 'number' &&
          !isNaN(totalWeight) &&
          totalWeight > 0 &&
          availableWeight !== null &&
          typeof availableWeight === 'number' &&
          !isNaN(availableWeight) &&
          availableWeight >= 0 ? (
            <GaugeChart value={availableWeight} total={totalWeight} />
          ) : (
            <span className='text--error' style={{ fontWeight: 'bold' }}>
              {I18n.translate('opportunities.form.no_available_inventory')}
            </span>
          ))}
        <OpportunityTextInput
          field={estimatedKgField}
          type='number'
          min={0}
          step='1'
          append='kg'
        />
        <div>
          <ControlledSearchSelectGroup
            namespace='opportunity'
            field={markIdField}
            overrideOptions={isFwd ? markOptions : filteredMarks}
            onChange={(value) => handleMarkSelect(value)}
            value={markId}
            disabled={disableMark}
            acceptEmptyValue
          />
        </div>
        <OpportunitySelectGroup
          field={certificateIdField}
          value={certificateIdField?.value}
        />
      </Grid>
    </FieldSetWithTitle>
  );
};
